import { MouseEvent, forwardRef } from "react";

import { mergeSx } from "../../../common/helpers/mergeSx";

import type { DashboardChipFilterItemProps } from "./types";

import { DashboardChip, type DashboardChipProps } from "@/dashboard-design-system/DashboardChip";

export const DashboardChipFilterItem = forwardRef<HTMLDivElement, DashboardChipFilterItemProps>(
  ({ value, variant, vibe, shape, onClick, selected = false, onChange, disabled, sx, ...rest }, ref) => {
    const resolvedVariant: DashboardChipProps["variant"] = variant ?? (selected ? "filled" : "outlined");
    const resolvedVibe: DashboardChipProps["vibe"] = vibe ?? "neutral";
    const resolvedShape: DashboardChipProps["shape"] = shape ?? "pill";

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }
      onClick?.(event);
      if (event.defaultPrevented) {
        return;
      }

      onChange?.(value);
    };

    return (
      <DashboardChip
        {...rest}
        ref={ref}
        variant={resolvedVariant}
        vibe={resolvedVibe}
        shape={resolvedShape}
        onClick={handleClick}
        aria-pressed={selected}
        data-selected={selected ? "true" : "false"}
        disabled={disabled}
        sx={mergeSx(
          (theme) => ({
            height: 28,
            paddingInline: 1.5,
            ...(!vibe &&
              !selected && {
                borderColor: theme.palette.border.neutral.quiet,
                backgroundColor: theme.palette.surface.neutral.supershy,
                ["&:hover"]: {
                  backgroundColor: theme.palette.surface.neutral.quiet,
                },
              }),
          }),
          sx
        )}
      />
    );
  }
);

DashboardChipFilterItem.displayName = "DashboardChipFilterItem";
